<template>
  <div>
    <!-- Search Input -->
    <form @submit.prevent="getSearchResults()" class="flex flex-row">
      <input
        type="text"
        class="input min-w-0 lg:text-lg rounded-xl py-3 px-4 mr-2"
        :placeholder="$t('Search ideas...')"
        v-model="term"
      />
      <button type="submit" class="btn-main px-5 py-3 lg:text-lg rounded-xl">
        <svg
          class="w-6 h-6 md:mr-2"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          ></path>
        </svg>
        <span class="hidden md:block">{{ $t("Search") }}</span>
      </button>
    </form>

    <!-- Search Filter Grid -->
    <div class="grid grid-cols-1 lg:grid-cols-7 gap-6 mt-8 mb-8">
      <!-- Sidebar -->
      <div class="col-span-1 lg:col-span-2">
        <div class="bg-white rounded-xl border overflow-hidden">
          <form @submit.prevent="getSearchResults()">
            <input type="hidden" name="utf8" value="✓" />

            <!-- Status Section -->
            <div class="search-filter__section">
              <div class="search-filter__header">
                <h5 class="search-filter__title">{{ $t("Status") }}</h5>
                <button
                  type="button"
                  @click="toggleStatusesSelection()"
                  class="search-filter__toggle"
                >
                  {{ isAllStatusesSelected ? $t("Deselect all") : $t("Select all") }}
                </button>
              </div>
              <div class="search-filter__options">
                <label
                  v-for="status in statuses"
                  :key="status.id"
                  :for="'status-' + status.id"
                  class="search-filter__option"
                >
                  <input
                    type="checkbox"
                    name="post[status][]"
                    :id="'status-' + status.id"
                    :value="status.id"
                    v-model="checkedStatuses"
                    class="search-filter__checkbox"
                  />
                  <span class="search-filter__option-label">{{ status.name }}</span>
                </label>
              </div>
            </div>

            <!-- Board Section -->
            <div class="search-filter__section">
              <div class="search-filter__header">
                <h5 class="search-filter__title">{{ $t("Board") }}</h5>
                <button
                  type="button"
                  @click="toggleBoardsSelection()"
                  class="search-filter__toggle"
                >
                  {{ isAllBoardsSelected ? $t("Deselect all") : $t("Select all") }}
                </button>
              </div>
              <div class="search-filter__options">
                <label
                  v-for="board in boards"
                  :key="board.id"
                  :for="'board-' + board.id"
                  class="search-filter__option"
                >
                  <input
                    type="checkbox"
                    name="post[board][]"
                    :id="'board-' + board.id"
                    :value="board.id"
                    v-model="checkedBoards"
                    class="search-filter__checkbox"
                  />
                  <span class="search-filter__option-label">{{ board.name }}</span>
                </label>
              </div>
            </div>

            <!-- Tags Section (Admin only) -->
            <div v-if="isAdmin && tags && tags.length > 0" class="search-filter__section">
              <div class="search-filter__header">
                <h5 class="search-filter__title">{{ $t("Tags") }}</h5>
              </div>
              <div class="search-filter__options">
                <label
                  v-for="tag in tags"
                  :key="tag.id"
                  :for="'tag-' + tag.id"
                  class="search-filter__option"
                >
                  <input
                    type="checkbox"
                    name="post[tag][]"
                    :id="'tag-' + tag.id"
                    :value="tag.id"
                    v-model="checkedTags"
                    class="search-filter__checkbox"
                  />
                  <span class="search-filter__option-label">{{ tag.name }}</span>
                </label>
              </div>
            </div>

            <!-- Filter Button -->
            <div class="search-filter__footer">
              <button type="submit" class="btn-main w-full justify-center rounded-lg">
                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                {{ $t("Filter results") }}
              </button>
            </div>
          </form>
        </div>
      </div>

      <!-- Results -->
      <div class="col-span-1 lg:col-span-5">
        <!-- Initial state: prompt to search -->
        <div
          v-if="term.length == 0 && submitted == false && posts.length == 0"
          class="flex flex-col items-center justify-center text-center py-12"
        >
          <div class="border-2 border-neutral-200 p-5 rounded-xl w-full max-w-xs flex bg-white items-center mb-6">
            <div class="p-2 bg-neutral-100 rounded-lg">
              <svg class="w-6 h-6 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
              </svg>
            </div>
            <div class="flex-grow ml-3">
              <div class="h-3 bg-neutral-200 rounded w-2/3"></div>
              <div class="h-3 bg-neutral-200 rounded w-1/2 mt-2"></div>
            </div>
          </div>
          <p class="text-neutral-900 font-medium">{{ $t("Search for ideas") }}</p>
          <p class="text-neutral-600 text-sm mt-1">{{ $t("Enter a search term above to find ideas") }}</p>
        </div>

        <!-- No results state -->
        <div
          v-if="posts.length === 0 && submitted === true"
          class="flex flex-col items-center justify-center text-center py-12"
        >
          <div class="border-2 border-neutral-200 p-5 rounded-xl w-full max-w-xs flex bg-white items-center mb-6">
            <div class="p-2 bg-neutral-100 rounded-lg">
              <svg class="w-6 h-6 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </div>
            <div class="flex-grow ml-3">
              <div class="h-3 bg-neutral-200 rounded w-2/3"></div>
              <div class="h-3 bg-neutral-200 rounded w-1/2 mt-2"></div>
            </div>
          </div>
          <p class="text-neutral-900 font-medium">{{ $t("No ideas found") }}</p>
          <p class="text-neutral-600 text-sm mt-1">{{ $t("No ideas match") }} "<span class="font-medium">{{ term }}</span>"</p>
          <p class="text-neutral-500 text-sm mt-1">{{ $t("Try a different search term or adjust your filters") }}</p>
        </div>

        <!-- Results found -->
        <div
          v-if="posts.length > 0 && submitted === true"
          class="bg-white rounded-xl border overflow-hidden mb-8"
        >
          <div class="px-4 py-3 border-b border-neutral-100 bg-neutral-50">
            <div class="flex items-center justify-between">
              <span class="text-neutral-800 font-semibold">{{ $t("Search results") }}</span>
              <span class="text-sm text-neutral-600">{{ posts.length }}{{ moreResults ? "+" : "" }} {{ posts.length > 1 || moreResults ? $t("ideas") : $t("idea") }}</span>
            </div>
          </div>
          <div
            class="flex flex-col"
            v-infinite-scroll="loadMore"
            :infinite-scroll-disabled="isLoadingMore || !moreResults"
            infinite-scroll-distance="200"
          >
            <div
              is="post"
              v-for="post in filteredPosts"
              :originalPost="post"
              :currentUser="JSON.parse(currentUser)"
              :current-project-admin="isAdmin"
              :key="post.id"
              :allow-anonymous="allowAnonymouslyVote"
              :status-name="JSON.parse(statusNames)"
              :show-board="true"
              :id="post.id"
              :class="post.id"
            ></div>
          </div>
          <div v-if="isLoadingMore" class="p-4 text-center">
            <div class="lds-ripple text-project-500 scale-75"><div></div><div></div></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import Post from "../post";
var infiniteScroll = require("vue-infinite-scroll");

export default {
  props: {
    searchUrl: String,
    allStatuses: String,
    isAdmin: Boolean,
    allBoards: String,
    currentUser: String,
    statusNames: String,
    postTags: String,
    allowAnonymouslyVote: Boolean,
    allPosts: String,
    searchTerm: String,
    currentUserVotes: Array,
    hasMore: Boolean,
  },
  components: {
    Post,
  },
  directives: { infiniteScroll },
  data() {
    return {
      term: this.searchTerm,
      statuses: JSON.parse(this.allStatuses || '[]'),
      boards: JSON.parse(this.allBoards || '[]'),
      tags: this.postTags ? JSON.parse(this.postTags) : [],
      checkedStatuses: [],
      checkedBoards: [],
      checkedTags: [],
      posts: JSON.parse(this.allPosts || '[]'),
      isAllStatusesSelected: false,
      isAllBoardsSelected: false,
      submitted: true,
      page: 0,
      moreResults: this.hasMore,
      isLoadingMore: false,
    };
  },
  created() {
    this.toggleStatusesSelection();
    this.toggleBoardsSelection();

    // listen for history changes
    window.addEventListener("popstate", () => {
      this.term = new URLSearchParams(window.location.search).get("term");
      this.getSearchResults();
    });
  },
  computed: {
    filteredPosts: {
      get() {
        return this.posts;
      },
      set(value) {
        this.posts = value;
      },
    },
  },
  methods: {
    toggleStatusesSelection() {
      if (this.isAllStatusesSelected) {
        this.checkedStatuses = [];
        this.isAllStatusesSelected = false;
      } else {
        this.statuses.forEach((element) => {
          this.checkedStatuses.push(element.id);
        });
        this.checkedStatuses = [...new Set(this.checkedStatuses)];
        this.isAllStatusesSelected = true;
      }
    },
    toggleBoardsSelection() {
      if (this.isAllBoardsSelected) {
        this.checkedBoards = [];
        this.isAllBoardsSelected = false;
      } else {
        this.boards.forEach((element) => {
          this.checkedBoards.push(element.id);
        });
        this.checkedBoards = [...new Set(this.checkedBoards)];
        this.isAllBoardsSelected = true;
      }
    },
    loadMore() {
      if (this.isLoadingMore || !this.moreResults) return;
      this.getSearchResults(false);
    },
    async getSearchResults(reset = true) {
      const page = reset ? 0 : this.page + 1;
      this.isLoadingMore = !reset;
      Rails.ajax({
        url:
          this.searchUrl +
          `/?term=${this.term}&status[]=${this.checkedStatuses}&board[]=${this.checkedBoards}&page=${page}`,
        type: "GET",
        success: (data) => {
          var posts = JSON.parse(data.posts);
          posts.forEach((post) => {
            post.current_user_voted_for = data.current_user_votes.includes(
              post.id
            );
          });
          if (reset) {
            this.filteredPosts = posts;
          } else {
            const seen = new Set(this.posts.map((post) => post.id));
            this.filteredPosts = this.posts.concat(posts.filter((post) => !seen.has(post.id)));
          }
          this.page = page;
          this.moreResults = data.has_more;
          this.isLoadingMore = false;
          this.submitted = true;
          // A page whose ideas were all hidden adds nothing to scroll past, so
          // the scroll trigger would never fire again.
          if (!reset && posts.length === 0 && this.moreResults) {
            this.$nextTick(() => this.loadMore());
          }
          if (!reset) return;

          // replace term in the URL
          history.pushState(
            {},
            null,
            `${window.location.origin}${window.location.pathname}?term=${this.term}`
          );
        },
        error: (data, status) => {
          this.isLoadingMore = false;
        },
      });
    },
  },
  watch: {
    checkedStatuses() {
      if (this.checkedStatuses.length < this.statuses.length) {
        this.isAllStatusesSelected = false;
      } else if (this.checkedStatuses.length == this.statuses.length) {
        this.isAllStatusesSelected = true;
      }
    },
    checkedBoards() {
      if (this.checkedBoards.length < this.boards.length) {
        this.isAllBoardsSelected = false;
      } else if (this.checkedBoards.length == this.boards.length) {
        this.isAllBoardsSelected = true;
      }
    },
    term() {
      this.submitted = false;
    },
  },
};
</script>

<style scoped>
/* Search Filter Sidebar */
.search-filter__section {
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e6e6e6;
}

.search-filter__section:last-of-type {
  border-bottom: none;
}

.search-filter__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
}

.search-filter__title {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #525252;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  margin: 0;
}

.search-filter__toggle {
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--accent-color, #009be5);
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  transition: opacity 150ms ease;
}

.search-filter__toggle:hover {
  opacity: 0.8;
}

.search-filter__options {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.search-filter__option {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.5rem 0.625rem;
  margin: 0 -0.625rem;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: background-color 150ms ease;
}

.search-filter__option:hover {
  background-color: #fafafa;
}

.search-filter__checkbox {
  flex-shrink: 0;
}

.search-filter__option-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #525252;
  transition: color 150ms ease;
}

.search-filter__option:hover .search-filter__option-label {
  color: #262626;
}

.search-filter__footer {
  padding: 1rem 1.25rem;
  background-color: #fafafa;
  border-top: 1px solid #e6e6e6;
}
</style>
